import { AFCuid, AFEvent, AppsFlyer } from 'appsflyer-capacitor-plugin';
import { AppsFlyerApi } from './appsflyer.types';

type AppsFlyerNativeSDKInitParams = {
  devKey: string;
  appId: string;
};

type AppsFlyerNativeSDKStatus = 'initial' | 'initializing' | 'ready' | 'failed';

export class AppsFlyerNativeSDK {
  private static status: AppsFlyerNativeSDKStatus = 'initial';
  private static cachedSdk: AppsFlyerApi | null = null;
  private static cachedParams: AppsFlyerNativeSDKInitParams | null = null;
  private static initPromise: Promise<AppsFlyerApi | null> | null = null;

  static async getSDK(params: AppsFlyerNativeSDKInitParams): Promise<AppsFlyerApi | null> {
    // Check if parameters have changed and reinitialize if needed
    if (
      this.status === 'ready' &&
      this.cachedSdk &&
      this.cachedParams &&
      (this.cachedParams.devKey !== params.devKey || this.cachedParams.appId !== params.appId)
    ) {
      // Reset state to force reinitialization with new parameters
      this.status = 'initial';
      this.cachedSdk = null;
      this.cachedParams = null;
      this.initPromise = null;
    }

    // Return cached SDK if already initialized
    if (this.status === 'ready' && this.cachedSdk) {
      return this.cachedSdk;
    }

    // Return existing promise if initialization is in progress
    if (this.status === 'initializing' && this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.performInitialization(params);
    return this.initPromise;
  }

  private static async performInitialization(params: AppsFlyerNativeSDKInitParams): Promise<AppsFlyerApi | null> {
    try {
      const { devKey, appId } = params;

      // Start initialization
      this.status = 'initializing';

      await AppsFlyer.initSDK({
        devKey,
        appID: appId,
        waitForATTUserAuthorization: 10,
        registerOnDeepLink: true,
        registerConversionListener: true,
      });

      // Cache the SDK and parameters, update status
      this.cachedSdk = {
        logEvent: (event: AFEvent) => AppsFlyer.logEvent(event),
        setCustomerUserId: (userId: AFCuid) => AppsFlyer.setCustomerUserId(userId),
      };
      this.cachedParams = { devKey, appId };
      this.status = 'ready';

      return this.cachedSdk;
    } catch (error) {
      // Reset state on error
      this.status = 'failed';
      this.cachedSdk = null;
      this.cachedParams = null;
      this.initPromise = null;

      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(`AppsFlyer Native SDK initialization failed: ${errorMessage}`);
      return null;
    }
  }
}
